/**
 * cdn-domain-operations-tab.tsx — Operations tab for a CDN domain (act-as). Beta.
 *
 * Three cards:
 *  1. Bindings — list multi-CDN vendor bindings, provision/sync, disable/enable/delete per
 *     binding, live-status polling every 10 s, job-group polling until completion.
 *  2. Logging — access log toggle + sampling rate.
 *  3. Report Recipients — table of email+frequency recipients; add / delete.
 *
 * Endpoints:
 *   GET    /api/v3/cdn/domains/:id/operations          (OperationsConfig)
 *   PUT    /api/v3/cdn/domains/:id/operations/logging
 *   GET    /api/v3/cdn/domains/:id/operations/bindings/status (BindingLiveStatus)
 *   GET    /api/v3/cdn/domains/:id/operations/job-group/:groupId
 *   GET    /api/v3/cdn/domains/:id/operations/report-recipients
 *   POST   /api/v3/cdn/domains/:id/operations/report-recipients
 *   DELETE /api/v3/cdn/domains/:id/operations/report-recipients/:recipientId
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNOperations,
  useActAsCDNBindingLiveStatus,
  useActAsCDNJobGroup,
  useActAsCDNReportRecipients,
  useProvisionActAsCDNBinding,
  useActAsCDNBindingAction,
  useAddActAsCDNReportRecipient,
  useDeleteActAsCDNReportRecipient,
  actAsCDNSecurityKeys,
  type CdnBinding,
  type BindingLiveStatus,
  type LoggingConfig,
  type AddRecipientRequest,
  type ReportRecipient,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Frequency options ───────────────────────────────────────────────────── */
type Frequency = 'daily' | 'weekly' | 'monthly';
const FREQUENCIES: Frequency[] = ['daily', 'weekly', 'monthly'];

/* ── Binding status pill ─────────────────────────────────────────────────── */
function BindingStatusPill({ status }: { status: string | undefined }) {
  const s = status ?? 'unknown';
  const tone = s === 'active' ? 'success'
    : s === 'disabled' ? 'warning'
    : s === 'provisioning' ? 'info'
    : s === 'error' ? 'danger'
    : 'neutral';
  return <Badge tone={tone} appearance="subtle" size="sm">{s}</Badge>;
}

/* ── Live-status indicator ───────────────────────────────────────────────── */
function LiveStatusDot({
  liveStatus,
  bindingId,
}: {
  liveStatus: BindingLiveStatus | null | undefined;
  bindingId: string;
}) {
  const entry = liveStatus?.bindings?.[bindingId];
  if (!entry) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
        background: entry.status === 'online' ? 'var(--vn-success-text)' : 'var(--vn-danger-text)',
      }} />
      {entry.status}{entry.latency_ms != null ? ` (${entry.latency_ms} ms)` : ''}
    </span>
  );
}

/* ── Binding row ─────────────────────────────────────────────────────────── */
interface BindingRowProps {
  binding: CdnBinding;
  liveStatus: BindingLiveStatus | null | undefined;
  domainId: string;
  onActionComplete: () => void;
}

function BindingRow({ binding, liveStatus, domainId, onActionComplete }: BindingRowProps) {
  const { t } = useTranslation();
  const actionMut = useActAsCDNBindingAction(domainId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleToggle = () => {
    const action = binding.status === 'active' ? 'disable' : 'enable';
    actionMut.mutate({ bindingId: binding.id, action }, { onSuccess: onActionComplete });
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)',
      padding: 'var(--vn-space-3)', borderRadius: 'var(--vn-radius-md)',
      background: 'var(--vn-bg-subtle)',
    }}>
      <div style={{ flex: 1, display: 'grid', gap: 'var(--vn-space-1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <Badge tone="info" appearance="outline" size="sm">{binding.provider}</Badge>
          <span style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)' }}>{binding.uid ?? '—'}</span>
          <BindingStatusPill status={binding.status} />
          <LiveStatusDot liveStatus={liveStatus} bindingId={binding.id} />
        </div>
        {binding.cname && (
          <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {binding.cname}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--vn-space-1)', alignItems: 'center' }}>
        <Button variant="ghost" size="sm" disabled={actionMut.isPending} onClick={handleToggle}>
          {binding.status === 'active'
            ? t('cdn_domain.operations_tab.action_disable', 'Disable')
            : t('cdn_domain.operations_tab.action_enable', 'Enable')}
        </Button>
        <Button
          variant="ghost" size="sm"
          style={{ color: 'var(--vn-danger-text)' }}
          aria-label={t('cdn_domain.operations_tab.action_delete_binding', 'Delete binding')}
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 size={14} />
        </Button>
      </div>

      <ActAsMutationConfirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('cdn_domain.operations_tab.confirm_delete_binding_title', 'Delete binding')}
        summary={t('cdn_domain.operations_tab.confirm_delete_binding_desc', 'Remove the {{provider}} binding for {{uid}}. This cannot be undone.', {
          provider: binding.provider,
          uid: binding.uid ?? '—',
        })}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.operations(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/operations/bindings/${binding.id}`)}
        onSuccess={onActionComplete}
        confirmLabel={t('cdn_domain.operations_tab.confirm_delete_label', 'Delete')}
        destructive
      />
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */

interface Props { domainId: string }

export function CdnDomainOperationsTab({ domainId }: Props) {
  const { t } = useTranslation();

  const opsQ     = useActAsCDNOperations(domainId);
  const statusQ  = useActAsCDNBindingLiveStatus(domainId, true);
  const recipQ   = useActAsCDNReportRecipients(domainId);
  const provMut  = useProvisionActAsCDNBinding(domainId);
  const addRecip = useAddActAsCDNReportRecipient(domainId);
  const delRecip = useDeleteActAsCDNReportRecipient(domainId);

  const [log, setLog]         = useState<LoggingConfig>({ access_log_enabled: false, sampling_rate: 1 });
  const [logSnap, setLogSnap] = useState<LoggingConfig>({ access_log_enabled: false, sampling_rate: 1 });
  const [logConfirm, setLogConfirm] = useState(false);

  const [groupId, setGroupId] = useState<string | null>(null);
  const jobQ = useActAsCDNJobGroup(domainId, groupId);

  const [newEmail, setNewEmail] = useState('');
  const [newFreq, setNewFreq]   = useState<Frequency>('weekly');

  useEffect(() => {
    const lc = opsQ.data?.logging;
    if (lc) { setLog(lc); setLogSnap(lc); }
  }, [opsQ.data]);

  useEffect(() => {
    if (jobQ.data?.status === 'completed' || jobQ.data?.status === 'failed') {
      const timer = setTimeout(() => setGroupId(null), 2000);
      void opsQ.refetch();
      return () => clearTimeout(timer);
    }
  }, [jobQ.data?.status, opsQ]);

  const logDirty = JSON.stringify(log) !== JSON.stringify(logSnap);

  const handleProvision = useCallback(() => {
    provMut.mutate(
      { provider: 'auto', acknowledge_cost: true },
      {
        onSuccess: (res) => {
          if (res.group_id) setGroupId(res.group_id);
          void opsQ.refetch();
        },
      },
    );
  }, [provMut, opsQ]);

  const handleAddRecipient = () => {
    if (!newEmail.trim()) return;
    const payload: AddRecipientRequest = { email: newEmail.trim(), frequency: newFreq };
    addRecip.mutate(payload, {
      onSuccess: () => { setNewEmail(''); setNewFreq('weekly'); void recipQ.refetch(); },
    });
  };

  if (opsQ.isLoading || recipQ.isLoading) return <Skeleton style={{ height: 500 }} />;
  if (opsQ.isError || recipQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.operations_tab.load_error', 'Failed to load operations settings')}
        onRetry={() => { void opsQ.refetch(); void recipQ.refetch(); }}
      />
    );
  }

  const bindings   = opsQ.data?.bindings ?? [];
  const liveStatus = statusQ.data;
  const recipients = recipQ.data ?? [];
  const jobRunning = Boolean(groupId) && jobQ.data?.status !== 'completed' && jobQ.data?.status !== 'failed';

  return (
    <Section
      title={t('cdn_domain.operations_tab.title', 'Operations')}
      description={t('cdn_domain.operations_tab.description', 'Manage CDN bindings, access logging, and report recipients.')}
    >
      <Alert tone="info" style={{ marginBottom: 'var(--vn-space-4)' }}>
        {t('cdn_domain.operations_tab.beta_notice', 'Beta — bindings, logging, and recipients are managed independently.')}
      </Alert>

      <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>

        {/* ── Bindings ── */}
        <Card>
          <CardHeader
            actions={
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                {jobRunning && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                <Button
                  variant="secondary" size="sm"
                  startIcon={<RefreshCw size={14} />}
                  disabled={statusQ.isFetching}
                  onClick={() => statusQ.refetch()}
                >
                  {t('cdn_domain.operations_tab.refresh_status', 'Refresh status')}
                </Button>
                <Button
                  size="sm"
                  startIcon={<Plus size={14} />}
                  disabled={provMut.isPending || jobRunning}
                  onClick={handleProvision}
                >
                  {t('cdn_domain.operations_tab.action_provision', 'Provision / sync')}
                </Button>
              </div>
            }
          >
            <CardTitle>{t('cdn_domain.operations_tab.section_bindings', 'CDN Bindings')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.operations_tab.section_bindings_desc', 'Active multi-CDN vendor bindings. Live status polls every 10 s.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {jobQ.data && groupId && (
              <Alert
                tone={jobQ.data.status === 'failed' ? 'danger' : 'info'}
                style={{ marginBottom: 'var(--vn-space-2)' }}
              >
                {t('cdn_domain.operations_tab.job_status', 'Provisioning job: {{status}}', { status: jobQ.data.status })}
                {jobQ.data.message && ` — ${jobQ.data.message}`}
              </Alert>
            )}
            {bindings.length === 0 ? (
              <EmptyState
                size="sm"
                title={t('cdn_domain.operations_tab.no_bindings', 'No bindings yet')}
                description={t('cdn_domain.operations_tab.no_bindings_desc', 'Click "Provision / sync" to create bindings for this domain.')}
              />
            ) : (
              bindings.map((b) => (
                <BindingRow
                  key={b.id}
                  binding={b}
                  liveStatus={liveStatus}
                  domainId={domainId}
                  onActionComplete={() => { void opsQ.refetch(); }}
                />
              ))
            )}
          </CardBody>
        </Card>

        {/* ── Logging ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.operations_tab.section_logging', 'Access Logging')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.operations_tab.section_logging_desc', 'Enable edge access logs and configure sampling rate.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title={t('cdn_domain.operations_tab.field_log_enabled', 'Access log enabled')}
              control={
                <Switch
                  checked={log.access_log_enabled ?? false}
                  onCheckedChange={(v) => setLog((l) => ({ ...l, access_log_enabled: v }))}
                />
              }
            />
            {log.access_log_enabled && (
              <Field
                label={t('cdn_domain.operations_tab.field_sampling_rate', 'Sampling rate (1–10 000)')}
                description={t('cdn_domain.operations_tab.field_sampling_rate_desc', 'Log 1 in N requests. 1 = every request.')}
              >
                {(p) => (
                  <NumericInput
                    {...p}
                    min={1}
                    max={10000}
                    value={log.sampling_rate ?? 1}
                    onChange={(e) => setLog((l) => ({ ...l, sampling_rate: Number(e.target.value) || 1 }))}
                    style={{ maxWidth: 180 }}
                  />
                )}
              </Field>
            )}
          </CardBody>
          <ActionBar
            end={
              <>
                <Button variant="secondary" disabled={!logDirty} onClick={() => setLog(logSnap)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button disabled={!logDirty} onClick={() => setLogConfirm(true)}>
                  {t('cdn_domain.operations_tab.save_logging', 'Save logging')}
                </Button>
              </>
            }
          />
        </Card>

        {/* ── Report recipients ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.operations_tab.section_recipients', 'Report Recipients')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.operations_tab.section_recipients_desc', 'Receive scheduled CDN usage and security reports by email.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-end' }}>
              <Field label={t('cdn_domain.operations_tab.field_recip_email', 'Email address')} style={{ flex: 1 }}>
                {(p) => (
                  <Input
                    {...p}
                    type="email"
                    value={newEmail}
                    placeholder="ops@example.com"
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddRecipient(); }}
                  />
                )}
              </Field>
              <Field label={t('cdn_domain.operations_tab.field_recip_freq', 'Frequency')}>
                {(p) => (
                  <Select value={newFreq} onValueChange={(v) => setNewFreq(v as Frequency)}>
                    <SelectTrigger {...p} style={{ width: 130 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Button
                startIcon={<Plus size={14} />}
                disabled={!newEmail.trim() || addRecip.isPending}
                onClick={handleAddRecipient}
              >
                {t('cdn_domain.operations_tab.action_add_recipient', 'Add')}
              </Button>
            </div>

            {recipients.length === 0 ? (
              <EmptyState
                size="sm"
                title={t('cdn_domain.operations_tab.no_recipients', 'No recipients yet')}
              />
            ) : (
              <DataTable<ReportRecipient>
                columns={useMemo<ColumnDef<ReportRecipient, unknown>[]>(() => [
                  { id: 'email', accessorKey: 'email', header: t('cdn_domain.operations_tab.col_email', 'Email') },
                  {
                    id: 'frequency', accessorKey: 'frequency', header: t('cdn_domain.operations_tab.col_frequency', 'Frequency'),
                    cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.frequency}</Badge>,
                  },
                  {
                    id: 'actions', header: '', size: 40,
                    cell: ({ row }) => (
                      <Button
                        variant="ghost" size="sm"
                        style={{ color: 'var(--vn-danger-text)' }}
                        aria-label={t('cdn_domain.operations_tab.action_delete_recipient', 'Delete recipient')}
                        disabled={delRecip.isPending}
                        onClick={() => delRecip.mutate(row.original.id, { onSuccess: () => { void recipQ.refetch(); } })}
                      >
                        <Trash2 size={13} />
                      </Button>
                    ),
                  },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                ], [delRecip.isPending, t])}
                data={recipients}
                dense
              />
            )}
          </CardBody>
        </Card>
      </div>

      <ActAsMutationConfirm
        open={logConfirm}
        onOpenChange={setLogConfirm}
        title={t('cdn_domain.operations_tab.confirm_log_title', 'Save logging settings')}
        summary={t(
          'cdn_domain.operations_tab.log_summary',
          'Access logging {{status}} with sampling rate 1:{{rate}}.',
          { status: log.access_log_enabled ? 'enabled' : 'disabled', rate: log.sampling_rate ?? 1 },
        )}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.operations(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/operations/logging`, log)}
        onSuccess={() => setLogSnap(log)}
      />
    </Section>
  );
}
